import {
  CONNECTION_HEALTH_FLAP_THRESHOLD,
  CONNECTION_HEALTH_FLAP_WINDOW_MS,
  CONNECTION_HEALTH_LAYER_DOWN_MS,
  CONNECTION_HEALTH_UNSTABLE_CLEAR_MS,
  CONNECTION_HEALTH_SESSION_WARMUP_MS,
} from "~/context/call/connectionHealth/constants";
import type {
  ConnectionFlapEventType,
  ConnectionHealthSnapshot,
} from "~/context/call/connectionHealth/types";

export type AidCallConnectionHealthSimulateLayer = "socket" | "sip";

export type AidCallConnectionHealthSimulateKind =
  | "disconnect"
  | "reconnect_attempt";

export type AidCallConnectionHealthSimulateFlapsOptions = {
  layer?: AidCallConnectionHealthSimulateLayer;
  kind?: AidCallConnectionHealthSimulateKind;
  gapMs?: number;
};

export type AidCallConnectionHealthDebugApi = {
  /** Current snapshot (same shape as `useCall().connectionHealth`). */
  snapshot: () => ConnectionHealthSnapshot;
  /** Active threshold constants (read-only). */
  thresholds: {
    flapThreshold: number;
    flapWindowMs: number;
    layerDownMs: number;
    unstableClearMs: number;
    sessionWarmupMs: number;
  };
  /** Inject a single flap for QA — does not touch live socket/SIP connections. */
  simulateFlap: (
    layer: AidCallConnectionHealthSimulateLayer,
    kind?: AidCallConnectionHealthSimulateKind
  ) => Promise<ConnectionHealthSnapshot>;
  /** Inject multiple flaps (default: 3 socket disconnects, 250ms apart). */
  simulateFlaps: (
    count?: number,
    options?: AidCallConnectionHealthSimulateFlapsOptions
  ) => Promise<ConnectionHealthSnapshot>;
  help: () => string;
};

declare global {
  interface Window {
    __aidcallConnectionHealth?: AidCallConnectionHealthDebugApi;
  }
}

const DEBUG_HELP = `window.__aidcallConnectionHealth — operator connection health (dev/QA)

Read state:
  __aidcallConnectionHealth.snapshot()
  __aidcallConnectionHealth.thresholds

Simulate flapping (injects counters only — live socket/SIP unchanged):
  await __aidcallConnectionHealth.simulateFlap('socket')
  await __aidcallConnectionHealth.simulateFlap('sip')
  await __aidcallConnectionHealth.simulateFlaps(3)
  await __aidcallConnectionHealth.simulateFlaps(3, { layer: 'sip', gapMs: 500 })

After ${CONNECTION_HEALTH_FLAP_THRESHOLD} flaps within ${CONNECTION_HEALTH_FLAP_WINDOW_MS / 1000}s
(both layers currently up, after initial connect), expect status "unstable" + banner + toast.
Startup grace: ${CONNECTION_HEALTH_SESSION_WARMUP_MS / 1000}s OR until first full connect — no unstable during warmup.
Flaps clear from "unstable" after ${CONNECTION_HEALTH_UNSTABLE_CLEAR_MS / 1000}s quiet.`;

const resolveSimulateEvent = (
  layer: AidCallConnectionHealthSimulateLayer,
  kind: AidCallConnectionHealthSimulateKind
): ConnectionFlapEventType => {
  if (layer === "socket") {
    return kind === "reconnect_attempt"
      ? "socket_reconnect_attempt"
      : "socket_disconnect";
  }

  return kind === "reconnect_attempt"
    ? "sip_unregister"
    : "sip_transport_disconnect";
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

export type RegisterAidCallConnectionHealthDebugParams = {
  getSnapshot: () => ConnectionHealthSnapshot;
  recordFlap: (eventType: ConnectionFlapEventType) => void;
};

export const registerAidCallConnectionHealthDebug = ({
  getSnapshot,
  recordFlap,
}: RegisterAidCallConnectionHealthDebugParams) => {
  const simulateFlap = async (
    layer: AidCallConnectionHealthSimulateLayer,
    kind: AidCallConnectionHealthSimulateKind = "disconnect"
  ) => {
    const eventType = resolveSimulateEvent(layer, kind);
    recordFlap(eventType);
    await sleep(50);
    return getSnapshot();
  };

  const api: AidCallConnectionHealthDebugApi = {
    snapshot: getSnapshot,
    thresholds: {
      flapThreshold: CONNECTION_HEALTH_FLAP_THRESHOLD,
      flapWindowMs: CONNECTION_HEALTH_FLAP_WINDOW_MS,
      layerDownMs: CONNECTION_HEALTH_LAYER_DOWN_MS,
      unstableClearMs: CONNECTION_HEALTH_UNSTABLE_CLEAR_MS,
      sessionWarmupMs: CONNECTION_HEALTH_SESSION_WARMUP_MS,
    },
    simulateFlap,
    simulateFlaps: async (count = CONNECTION_HEALTH_FLAP_THRESHOLD, options = {}) => {
      const layer = options.layer ?? "socket";
      const kind = options.kind ?? "disconnect";
      const gapMs = options.gapMs ?? 250;
      const safeCount = Math.max(1, Math.floor(count));

      for (let index = 0; index < safeCount; index += 1) {
        await simulateFlap(layer, kind);
        if (index < safeCount - 1) {
          await sleep(gapMs);
        }
      }

      return getSnapshot();
    },
    help: () => DEBUG_HELP,
  };

  window.__aidcallConnectionHealth = api;
};

export const unregisterAidCallConnectionHealthDebug = () => {
  delete window.__aidcallConnectionHealth;
};
